'use client';

import Script from "next/script";
import { useCallback, useEffect, useRef, useState } from "react";

import styles from "@/Components/Contact/contact.module.css";

const testTurnstileSiteKey = "1x00000000000000000000AA";
const turnstileSiteKey =
    process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY ||
    (process.env.NODE_ENV !== "production" ? testTurnstileSiteKey : "");
const isTurnstileConfigured = Boolean(turnstileSiteKey);

const createInitialFormData = () => ({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "",
    formStartedAt: String(Date.now()),
    turnstileToken: "",
});

export default function Contact() {
    const [formData, setFormData] = useState(createInitialFormData);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [statusMessage, setStatusMessage] = useState("");
    const [isTurnstileScriptLoaded, setIsTurnstileScriptLoaded] = useState(false);
    const [turnstileStatus, setTurnstileStatus] = useState(
        isTurnstileConfigured ? "loading" : "missing-config"
    );
    const turnstileContainerRef = useRef(null);
    const turnstileWidgetIdRef = useRef(null);

    const validate = (values) => {
        const nextErrors = {};

        if (!values.name.trim()) nextErrors.name = "Name is required.";
        if (!values.email.trim()) {
            nextErrors.email = "Email is required.";
        } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(values.email)) {
            nextErrors.email = "Enter a valid email address.";
        }

        if (values.phone && !/^\+?[\d()\-\s]{7,20}$/.test(values.phone)) {
            nextErrors.phone = "Enter a valid phone number.";
        }

        if (!values.message.trim()) nextErrors.message = "Message is required.";
        if (!isTurnstileConfigured) {
            nextErrors.turnstile = "Contact verification is not configured.";
        } else if (!values.turnstileToken) {
            nextErrors.turnstile = "Complete the verification.";
        }

        return nextErrors;
    };

    const clearFieldError = (fieldName) => {
        setErrors((current) => {
            if (!current[fieldName]) {
                return current;
            }

            const nextErrors = { ...current };
            delete nextErrors[fieldName];
            return nextErrors;
        });
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData((current) => ({ ...current, [name]: value }));
        clearFieldError(name);
    };

    const resetTurnstile = useCallback(() => {
        if (!isTurnstileConfigured) {
            return;
        }

        setFormData((current) => ({ ...current, turnstileToken: "" }));

        if (
            typeof window !== "undefined" &&
            window.turnstile &&
            turnstileWidgetIdRef.current !== null
        ) {
            window.turnstile.reset(turnstileWidgetIdRef.current);
            setTurnstileStatus("pending");
        }
    }, []);

    const renderTurnstile = useCallback(() => {
        if (
            !isTurnstileConfigured ||
            !isTurnstileScriptLoaded ||
            !turnstileContainerRef.current ||
            typeof window === "undefined" ||
            !window.turnstile ||
            turnstileWidgetIdRef.current !== null
        ) {
            return;
        }

        turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: turnstileSiteKey,
            theme: "auto",
            callback: (token) => {
                setFormData((current) => ({ ...current, turnstileToken: token }));
                clearFieldError("turnstile");
                setTurnstileStatus("verified");
            },
            "expired-callback": () => {
                setFormData((current) => ({ ...current, turnstileToken: "" }));
                setTurnstileStatus("expired");
            },
            "error-callback": () => {
                setFormData((current) => ({ ...current, turnstileToken: "" }));
                setTurnstileStatus("error");
            },
        });

        setTurnstileStatus("pending");
    }, [isTurnstileScriptLoaded]);

    useEffect(() => {
        renderTurnstile();

        return () => {
            if (
                typeof window !== "undefined" &&
                window.turnstile &&
                turnstileWidgetIdRef.current !== null
            ) {
                window.turnstile.remove(turnstileWidgetIdRef.current);
                turnstileWidgetIdRef.current = null;
            }
        };
    }, [renderTurnstile]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        const validationErrors = validate(formData);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setStatus("error");
            setStatusMessage("Please correct the highlighted fields.");
            return;
        }

        setStatus("submitting");
        setStatusMessage("");

        try {
            const response = await fetch("/api/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok || payload.success === false) {
                throw new Error(payload.error || "Request failed");
            }

            setStatus("success");
            setStatusMessage("Message sent successfully.");
            setFormData(createInitialFormData());
            setErrors({});
            resetTurnstile();
        } catch (error) {
            setStatus("error");
            setStatusMessage(
                error instanceof Error && error.message
                    ? error.message
                    : "Something went wrong. Try again."
            );
            resetTurnstile();
        }
    };

    const fieldErrorId = (fieldName) => `contact-${fieldName}-error`;
    const turnstileMessage =
        errors.turnstile ||
        (!isTurnstileConfigured
            ? "Contact verification is not configured."
            : turnstileStatus === "loading"
                ? "Loading verification..."
                : turnstileStatus === "expired"
                    ? "Verification expired. Complete it again."
                    : turnstileStatus === "error"
                        ? "Verification could not load. Try refreshing the page."
                        : "");

    return (
        <div className={styles.contactContainer}>
            {isTurnstileConfigured && (
                <Script
                    src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
                    strategy="afterInteractive"
                    onLoad={() => setIsTurnstileScriptLoaded(true)}
                    onError={() => setTurnstileStatus("error")}
                />
            )}

            <form className={styles.contactForm} onSubmit={handleSubmit} noValidate>
                <h2>Send us message</h2>

                <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="contact-name">Name</label>
                    <input
                        id="contact-name"
                        type="text"
                        name="name"
                        autoComplete="name"
                        value={formData.name}
                        onChange={handleChange}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? fieldErrorId("name") : undefined}
                    />
                    {errors.name && <p id={fieldErrorId("name")} className={styles.fieldError}>{errors.name}</p>}
                </div>

                <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="contact-email">Email</label>
                    <input
                        id="contact-email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? fieldErrorId("email") : undefined}
                    />
                    {errors.email && <p id={fieldErrorId("email")} className={styles.fieldError}>{errors.email}</p>}
                </div>

                <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="contact-phone">Phone Number</label>
                    <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        autoComplete="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={errors.phone ? fieldErrorId("phone") : undefined}
                    />
                    {errors.phone && <p id={fieldErrorId("phone")} className={styles.fieldError}>{errors.phone}</p>}
                </div>

                <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="contact-message">Message</label>
                    <textarea
                        id="contact-message"
                        name="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? fieldErrorId("message") : undefined}
                    />
                    {errors.message && <p id={fieldErrorId("message")} className={styles.fieldError}>{errors.message}</p>}
                </div>

                <div className={styles.turnstileField}>
                    {isTurnstileConfigured && <div ref={turnstileContainerRef} className={styles.turnstileWidget} />}
                    {turnstileMessage && (
                        <p className={errors.turnstile || turnstileStatus === "error" || turnstileStatus === "expired" ? styles.fieldError : styles.fieldNote}>
                            {turnstileMessage}
                        </p>
                    )}
                </div>

                <input
                    type="hidden"
                    name="formStartedAt"
                    value={formData.formStartedAt}
                    readOnly
                />

                <input
                    type="hidden"
                    name="turnstileToken"
                    value={formData.turnstileToken}
                    readOnly
                />

                <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className={styles.honeypot}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                />

                {status !== "idle" && (
                    <div
                        className={`${styles.buttonStatic} ${status === "error" ? styles.error : status === "success" ? styles.success : ""}`}
                        role={status === "error" ? "alert" : "status"}
                        aria-live="polite"
                    >
                        {status === "submitting" ? "Sending..." : statusMessage}
                    </div>
                )}

                <button
                    type="submit"
                    className={styles.button}
                    disabled={
                        status === "submitting" ||
                        !isTurnstileConfigured ||
                        turnstileStatus === "loading" ||
                        turnstileStatus === "error"
                    }
                >
                    {status === "submitting" ? "Sending..." : "Send"}
                </button>
            </form>
        </div>
    );
}
